import React, { useEffect, useState, useCallback } from 'react';
import { authManager } from '../utils/auth';

const Base_Url = process.env.REACT_APP_API_BASE_URL;

const SOURCE_TABS = [
    { value: 'all', label: 'All' },
    { value: 'circleone', label: 'Circle One' },
    { value: 'mml', label: 'MML' },
    { value: 'somocares', label: 'Somo Cares' },
];

const StatCard = ({ icon, iconClass, label, value, sub }) => (
    <div className="col-12 col-sm-6 col-lg-3 mb-4">
        <div className="card h-100">
            <div className="card-body d-flex align-items-center" style={{ gap: '16px' }}>
                <div style={{ fontSize: '2.2rem', width: '48px', textAlign: 'center' }}>
                    <i className={`fa-solid ${icon} ${iconClass}`} />
                </div>
                <div>
                    <div className="text-muted" style={{ fontSize: '0.78rem', textTransform: 'uppercase', letterSpacing: '0.06em' }}>{label}</div>
                    <div className="fwt_600" style={{ fontSize: '1.6rem', lineHeight: 1.2 }}>
                        {value ?? <span className="spinner-border spinner-border-sm text-secondary" />}
                    </div>
                    {sub && <div className="text-muted" style={{ fontSize: '0.75rem' }}>{sub}</div>}
                </div>
            </div>
        </div>
    </div>
);

const SourceBar = ({ label, count, total, colorClass }) => {
    const pct = total > 0 ? Math.round((count / total) * 100) : 0;
    return (
        <div className="mb-3">
            <div className="d-flex justify-content-between mb-1" style={{ fontSize: '0.85rem' }}>
                <span className="text-capitalize">{label}</span>
                <span className="fwt_600">{count.toLocaleString()} <span className="text-muted fwt_400">({pct}%)</span></span>
            </div>
            <div className="progress" style={{ height: '6px', borderRadius: '3px' }}>
                <div className={`progress-bar ${colorClass}`} style={{ width: `${pct}%`, borderRadius: '3px' }} />
            </div>
        </div>
    );
};

const SOURCE_META = {
    circleone: { label: 'Circle One', colorClass: 'bg-primary' },
    mml:       { label: 'MML',         colorClass: 'bg-success' },
    somocares: { label: 'Somo Cares',  colorClass: 'bg-warning' },
    all:       { label: 'All Access',  colorClass: 'bg-info'    },
};

const Dashboard = () => {
    const [stats, setStats] = useState(null);
    const [loading, setLoading] = useState(false);
    const [userRole, setUserRole] = useState(null);
    const [activeTab, setActiveTab] = useState('all');
    const [fetchError, setFetchError] = useState(false);

    const fetchStats = useCallback(async (source) => {
        setLoading(true);
        setFetchError(false);
        try {
            const param = source && source !== 'all' ? `?source=${source}` : '';
            const res = await authManager.authenticatedFetch(`${Base_Url}/users/stats${param}`);
            if (!res.ok) throw new Error('Failed');
            const data = await res.json();
            setStats(data.result || data.data);
        } catch {
            setFetchError(true);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        const init = async () => {
            const role = await authManager.validateUserRole();
            setUserRole(role);
            await fetchStats('all');
        };
        init();
    }, [fetchStats]);

    const handleTabClick = (tab) => {
        setActiveTab(tab);
        fetchStats(tab);
    };

    const isSuperAdmin = userRole === 'superadmin';
    const bySource = stats?.bySource || {};

    return (
        <div>
            <div className="content-header">
                <div className="container-fluid">
                    <div className="row mb-2">
                        <div className="col-sm-6">
                            <h1 className="m-0 txt_lft">Dashboard</h1>
                        </div>
                    </div>
                </div>
            </div>

            <div className="content">
                <div className="container-fluid">

                    {fetchError && (
                        <div className="alert alert-warning mb-3">
                            Could not load stats. Please refresh or check your connection.
                        </div>
                    )}

                    {/* ── Source Tabs (superadmin only) ── */}
                    {isSuperAdmin && (
                        <div className="mb-4">
                            <ul className="nav nav-pills" style={{ gap: '6px' }}>
                                {SOURCE_TABS.map(tab => (
                                    <li className="nav-item" key={tab.value}>
                                        <button
                                            type="button"
                                            className={`nav-link${activeTab === tab.value ? ' active' : ''}`}
                                            onClick={() => handleTabClick(tab.value)}
                                            style={{ minWidth: '90px', textAlign: 'center' }}
                                        >
                                            {tab.label}
                                            {tab.value !== 'all' && bySource[tab.value] !== undefined && (
                                                <span
                                                    className="badge badge-pill ml-2"
                                                    style={{
                                                        background: activeTab === tab.value ? 'rgba(255,255,255,0.3)' : '#e2e8f0',
                                                        color: activeTab === tab.value ? '#fff' : '#555',
                                                        fontSize: '0.7rem',
                                                    }}
                                                >
                                                    {(bySource[tab.value] || 0).toLocaleString()}
                                                </span>
                                            )}
                                        </button>
                                    </li>
                                ))}
                            </ul>
                        </div>
                    )}

                    {/* ── Stat Cards ── */}
                    <div className="row" style={{ opacity: loading ? 0.6 : 1, transition: 'opacity 0.2s' }}>
                        <StatCard
                            icon="fa-users"
                            iconClass="text-primary"
                            label="Total Users"
                            value={stats ? stats.totalUsers.toLocaleString() : null}
                        />
                        <StatCard
                            icon="fa-user-check"
                            iconClass="f_green"
                            label="Active Users"
                            value={stats ? stats.activeUsers.toLocaleString() : null}
                            sub={stats ? `${stats.pendingUsers.toLocaleString()} pending registration` : null}
                        />
                        <StatCard
                            icon="fa-credit-card"
                            iconClass="text-warning"
                            label="Paid Members"
                            value={stats ? stats.paidUsers.toLocaleString() : null}
                            sub={stats ? `${stats.trialUsers} trial · ${stats.freeUsers.toLocaleString()} free` : null}
                        />
                        <StatCard
                            icon="fa-user-plus"
                            iconClass="text-info"
                            label="New This Month"
                            value={stats ? stats.newThisMonth.toLocaleString() : null}
                        />
                    </div>

                    {/* ── Bottom charts ── */}
                    {stats && (
                        <div className="row mt-2">
                            {/* Source breakdown — superadmin all-tab only */}
                            {isSuperAdmin && activeTab === 'all' && (
                                <div className="col-12 col-md-6 col-lg-5 mb-4">
                                    <div className="card h-100">
                                        <div className="card-header">
                                            <h5 className="card-title m-0" style={{ fontSize: '0.85rem', textTransform: 'uppercase', letterSpacing: '0.06em' }}>
                                                Users by Source
                                            </h5>
                                        </div>
                                        <div className="card-body">
                                            {Object.entries(SOURCE_META).map(([key, meta]) => (
                                                <SourceBar
                                                    key={key}
                                                    label={meta.label}
                                                    count={bySource[key] ?? 0}
                                                    total={stats.totalUsers}
                                                    colorClass={meta.colorClass}
                                                />
                                            ))}
                                        </div>
                                    </div>
                                </div>
                            )}

                            {/* Membership breakdown — always shown */}
                            <div className="col-12 col-md-6 col-lg-5 mb-4">
                                <div className="card h-100">
                                    <div className="card-header">
                                        <h5 className="card-title m-0" style={{ fontSize: '0.85rem', textTransform: 'uppercase', letterSpacing: '0.06em' }}>
                                            Membership Breakdown
                                            {isSuperAdmin && activeTab !== 'all' && (
                                                <span className="text-muted ml-2" style={{ fontSize: '0.75rem', textTransform: 'none', letterSpacing: 0 }}>
                                                    — {SOURCE_TABS.find(t => t.value === activeTab)?.label}
                                                </span>
                                            )}
                                        </h5>
                                    </div>
                                    <div className="card-body">
                                        <SourceBar label="Paid"  count={stats.paidUsers}  total={stats.totalUsers} colorClass="bg-success"   />
                                        <SourceBar label="Trial" count={stats.trialUsers} total={stats.totalUsers} colorClass="bg-warning"   />
                                        <SourceBar label="Free"  count={stats.freeUsers}  total={stats.totalUsers} colorClass="bg-secondary" />
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}

                </div>
            </div>
        </div>
    );
};

export default Dashboard;
